<script>
    import { onMount } from 'svelte';
    import { getApiBase } from './apiBase.js';

    const apiBase = getApiBase();

    // Data
    let families = [];
    let students = [];

    // UI state
    let isLoading = false;
    let loadError = '';
    let successMessage = '';
    let errorMessage = '';

    // Create-family form state
    let newFamilyName = '';
    let newFamilyMembers = []; // array of student objects {id, name, email}
    let createQuery = '';
    let showCreateDropdown = false;
    let isCreating = false;

    // Edit state (only one family edited at a time)
    let editingFamilyId = null;
    let editName = '';
    let editMembers = [];
    let editQuery = '';
    let showEditDropdown = false;
    let isSavingEdit = false;

    onMount(async () => {
        await Promise.all([loadFamilies(), loadStudents()]);
    });

    function authHeaders() {
        const token = localStorage.getItem('jwtToken');
        return {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
        };
    }

    function handleAuthFailure() {
        alert('Reikia prisijungti iš naujo.');
        localStorage.removeItem('jwtToken');
        window.location.href = '#/about';
    }

    async function loadFamilies() {
        isLoading = true;
        loadError = '';
        try {
            const res = await fetch(`${apiBase}/studentplayground/api/admin/families/`, {
                headers: authHeaders(),
            });
            if (res.status === 401) return handleAuthFailure();
            const data = await res.json();
            if (res.ok && data.success) {
                families = data.families || [];
            } else {
                loadError = data.error || `Failed to load families (HTTP ${res.status})`;
            }
        } catch (e) {
            console.error('Error loading families:', e);
            loadError = 'Network error while loading families.';
        } finally {
            isLoading = false;
        }
    }

    async function loadStudents() {
        try {
            const res = await fetch(`${apiBase}/studentplayground/api/students-list/`, {
                headers: authHeaders(),
            });
            if (res.status === 401) return handleAuthFailure();
            const data = await res.json();
            if (res.ok && data.status === 'success') {
                students = data.students || [];
            }
        } catch (e) {
            console.error('Error loading students:', e);
        }
    }

    function clearMessages() {
        errorMessage = '';
        successMessage = '';
    }

    // Map of userId -> list of family names they already belong to.
    // Used to warn admins when a user is already in another family.
    $: membershipMap = (() => {
        const map = {};
        for (const fam of families) {
            for (const child of fam.children) {
                if (!map[child.id]) map[child.id] = [];
                map[child.id].push(fam.name);
            }
        }
        return map;
    })();

    function otherFamiliesFor(userId, excludeFamilyId = null) {
        // Returns family names (other than excludeFamilyId's current saved state) the user belongs to.
        const names = [];
        for (const fam of families) {
            if (excludeFamilyId !== null && fam.id === excludeFamilyId) continue;
            if (fam.children.some((c) => c.id === userId)) names.push(fam.name);
        }
        return names;
    }

    function filterStudents(query, excludeMembers) {
        const q = query.trim().toLowerCase();
        if (!q) return [];
        const excludeIds = new Set(excludeMembers.map((m) => m.id));
        return students
            .filter((s) => !excludeIds.has(s.id))
            .filter(
                (s) =>
                    (s.name && s.name.toLowerCase().includes(q)) ||
                    (s.email && s.email.toLowerCase().includes(q)) ||
                    (s.username && s.username.toLowerCase().includes(q))
            )
            .slice(0, 8);
    }

    $: createResults = filterStudents(createQuery, newFamilyMembers);
    $: editResults = filterStudents(editQuery, editMembers);

    // ---- Create family ----
    function addCreateMember(student) {
        if (!newFamilyMembers.some((m) => m.id === student.id)) {
            newFamilyMembers = [...newFamilyMembers, student];
        }
        createQuery = '';
        showCreateDropdown = false;
    }

    function removeCreateMember(id) {
        newFamilyMembers = newFamilyMembers.filter((m) => m.id !== id);
    }

    async function createFamily() {
        clearMessages();
        const name = newFamilyName.trim();
        if (!name) {
            errorMessage = 'Please enter a family name.';
            return;
        }
        isCreating = true;
        try {
            const res = await fetch(`${apiBase}/studentplayground/api/admin/families/create/`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({
                    name,
                    children: newFamilyMembers.map((m) => m.id),
                }),
            });
            if (res.status === 401) return handleAuthFailure();
            const data = await res.json();
            if (res.ok && data.success) {
                successMessage = `✅ Family "${data.family.name}" created.`;
                newFamilyName = '';
                newFamilyMembers = [];
                createQuery = '';
                await loadFamilies();
                setTimeout(() => (successMessage = ''), 5000);
            } else {
                errorMessage = data.error || 'Failed to create family.';
            }
        } catch (e) {
            console.error('Error creating family:', e);
            errorMessage = 'Network error. Please try again.';
        } finally {
            isCreating = false;
        }
    }

    // ---- Edit family ----
    function startEdit(family) {
        clearMessages();
        editingFamilyId = family.id;
        editName = family.name;
        editMembers = family.children.map((c) => ({ ...c }));
        editQuery = '';
        showEditDropdown = false;
    }

    function cancelEdit() {
        editingFamilyId = null;
        editName = '';
        editMembers = [];
        editQuery = '';
        showEditDropdown = false;
    }

    function addEditMember(student) {
        if (!editMembers.some((m) => m.id === student.id)) {
            editMembers = [...editMembers, student];
        }
        editQuery = '';
        showEditDropdown = false;
    }

    function removeEditMember(id) {
        editMembers = editMembers.filter((m) => m.id !== id);
    }

    async function saveEdit() {
        clearMessages();
        const name = editName.trim();
        if (!name) {
            errorMessage = 'Family name cannot be empty.';
            return;
        }
        isSavingEdit = true;
        try {
            const res = await fetch(
                `${apiBase}/studentplayground/api/admin/families/${editingFamilyId}/update/`,
                {
                    method: 'POST',
                    headers: authHeaders(),
                    body: JSON.stringify({
                        name,
                        children: editMembers.map((m) => m.id),
                    }),
                }
            );
            if (res.status === 401) return handleAuthFailure();
            const data = await res.json();
            if (res.ok && data.success) {
                successMessage = `✅ Family "${data.family.name}" updated.`;
                cancelEdit();
                await loadFamilies();
                setTimeout(() => (successMessage = ''), 5000);
            } else {
                errorMessage = data.error || 'Failed to update family.';
            }
        } catch (e) {
            console.error('Error updating family:', e);
            errorMessage = 'Network error. Please try again.';
        } finally {
            isSavingEdit = false;
        }
    }

    // ---- Delete family ----
    async function deleteFamily(family) {
        clearMessages();
        const confirmed = window.confirm(
            `Delete family "${family.name}"?\n\nThis removes the family grouping only — the ${family.children.length} member account(s) will NOT be deleted.`
        );
        if (!confirmed) return;
        try {
            const res = await fetch(
                `${apiBase}/studentplayground/api/admin/families/${family.id}/delete/`,
                {
                    method: 'DELETE',
                    headers: authHeaders(),
                }
            );
            if (res.status === 401) return handleAuthFailure();
            const data = await res.json();
            if (res.ok && data.success) {
                successMessage = `🗑️ Family "${family.name}" deleted.`;
                if (editingFamilyId === family.id) cancelEdit();
                await loadFamilies();
                setTimeout(() => (successMessage = ''), 5000);
            } else {
                errorMessage = data.error || 'Failed to delete family.';
            }
        } catch (e) {
            console.error('Error deleting family:', e);
            errorMessage = 'Network error. Please try again.';
        }
    }
</script>

<div class="family-manager">
    <div class="header">
        <h2>👪 Family Connections</h2>
        <p>Create, edit, and delete family groupings of student accounts.</p>
    </div>

    {#if successMessage}
        <div class="success-message">{successMessage}</div>
    {/if}
    {#if errorMessage}
        <div class="error-message">{errorMessage}</div>
    {/if}

    <!-- Create family -->
    <section class="panel">
        <h3>Create New Family</h3>
        <div class="form-group">
            <label for="newFamilyName">Family Name <span class="required">*</span></label>
            <input
                id="newFamilyName"
                type="text"
                bind:value={newFamilyName}
                on:input={clearMessages}
                placeholder="E.g. 'The Smith Household'"
                autocomplete="off"
            />
        </div>

        <div class="form-group">
            <label for="createQuery">Add Members</label>
            <div class="user-search-container">
                <input
                    id="createQuery"
                    type="text"
                    class="search-input"
                    bind:value={createQuery}
                    on:input={() => {
                        showCreateDropdown = createQuery.trim().length > 0;
                        clearMessages();
                    }}
                    placeholder="Search students by name or email..."
                    autocomplete="off"
                />
                {#if showCreateDropdown && createResults.length > 0}
                    <div class="search-results">
                        {#each createResults as student (student.id)}
                            <div
                                class="search-result-item"
                                role="button"
                                tabindex="0"
                                on:click={() => addCreateMember(student)}
                                on:keydown={(e) => (e.key === 'Enter' || e.key === ' ') && addCreateMember(student)}
                            >
                                <div class="user-info">{student.name}</div>
                                <div class="user-email">{student.email}</div>
                                {#if otherFamiliesFor(student.id).length > 0}
                                    <div class="member-warning">
                                        ⚠️ Already in: {otherFamiliesFor(student.id).join(', ')}
                                    </div>
                                {/if}
                            </div>
                        {/each}
                    </div>
                {/if}
            </div>
            <div class="form-help">Start typing to search. Only student accounts are shown.</div>
        </div>

        {#if newFamilyMembers.length > 0}
            <div class="chips">
                {#each newFamilyMembers as member (member.id)}
                    <span class="chip">
                        {member.name}
                        {#if otherFamiliesFor(member.id).length > 0}
                            <span class="chip-warning" title={`Already in: ${otherFamiliesFor(member.id).join(', ')}`}>⚠️</span>
                        {/if}
                        <button type="button" class="chip-remove" on:click={() => removeCreateMember(member.id)}>×</button>
                    </span>
                {/each}
            </div>
        {/if}

        <button class="btn btn-primary" on:click={createFamily} disabled={isCreating}>
            {#if isCreating}Creating…{:else}Create Family{/if}
        </button>
    </section>

    <!-- Existing families -->
    <section class="panel">
        <h3>Existing Families {#if families.length}<span class="count">({families.length})</span>{/if}</h3>

        {#if isLoading}
            <div class="muted">Loading…</div>
        {:else if loadError}
            <div class="error-message">{loadError}</div>
        {:else if families.length === 0}
            <div class="muted">No families yet.</div>
        {:else}
            <div class="family-list">
                {#each families as family (family.id)}
                    <div class="family-card">
                        {#if editingFamilyId === family.id}
                            <!-- Edit mode -->
                            <div class="form-group">
                                <label for={`editName-${family.id}`}>Family Name <span class="required">*</span></label>
                                <input
                                    id={`editName-${family.id}`}
                                    type="text"
                                    bind:value={editName}
                                    on:input={clearMessages}
                                    autocomplete="off"
                                />
                            </div>

                            <div class="form-group">
                                <label for={`editQuery-${family.id}`}>Add Members</label>
                                <div class="user-search-container">
                                    <input
                                        id={`editQuery-${family.id}`}
                                        type="text"
                                        class="search-input"
                                        bind:value={editQuery}
                                        on:input={() => {
                                            showEditDropdown = editQuery.trim().length > 0;
                                            clearMessages();
                                        }}
                                        placeholder="Search students by name or email..."
                                        autocomplete="off"
                                    />
                                    {#if showEditDropdown && editResults.length > 0}
                                        <div class="search-results">
                                            {#each editResults as student (student.id)}
                                                <div
                                                    class="search-result-item"
                                                    role="button"
                                                    tabindex="0"
                                                    on:click={() => addEditMember(student)}
                                                    on:keydown={(e) => (e.key === 'Enter' || e.key === ' ') && addEditMember(student)}
                                                >
                                                    <div class="user-info">{student.name}</div>
                                                    <div class="user-email">{student.email}</div>
                                                    {#if otherFamiliesFor(student.id, family.id).length > 0}
                                                        <div class="member-warning">
                                                            ⚠️ Already in: {otherFamiliesFor(student.id, family.id).join(', ')}
                                                        </div>
                                                    {/if}
                                                </div>
                                            {/each}
                                        </div>
                                    {/if}
                                </div>
                            </div>

                            <div class="chips">
                                {#each editMembers as member (member.id)}
                                    <span class="chip">
                                        {member.name}
                                        {#if otherFamiliesFor(member.id, family.id).length > 0}
                                            <span class="chip-warning" title={`Already in: ${otherFamiliesFor(member.id, family.id).join(', ')}`}>⚠️</span>
                                        {/if}
                                        <button type="button" class="chip-remove" on:click={() => removeEditMember(member.id)}>×</button>
                                    </span>
                                {/each}
                                {#if editMembers.length === 0}
                                    <span class="muted">No members.</span>
                                {/if}
                            </div>

                            <div class="card-actions">
                                <button class="btn btn-primary" on:click={saveEdit} disabled={isSavingEdit}>
                                    {#if isSavingEdit}Saving…{:else}Save{/if}
                                </button>
                                <button class="btn btn-secondary" on:click={cancelEdit} disabled={isSavingEdit}>Cancel</button>
                            </div>
                        {:else}
                            <!-- View mode -->
                            <div class="family-head">
                                <div class="family-name">{family.name}</div>
                                <div class="card-actions">
                                    <button class="btn btn-secondary" on:click={() => startEdit(family)}>Edit</button>
                                    <button class="btn btn-danger" on:click={() => deleteFamily(family)}>Delete</button>
                                </div>
                            </div>
                            {#if family.children.length > 0}
                                <div class="chips">
                                    {#each family.children as member (member.id)}
                                        <span class="chip chip-static" title={member.email}>{member.name}</span>
                                    {/each}
                                </div>
                            {:else}
                                <div class="muted">No members.</div>
                            {/if}
                        {/if}
                    </div>
                {/each}
            </div>
        {/if}
    </section>
</div>

<style>
    .family-manager {
        max-width: 720px;
        margin: 0 auto;
        padding: 2rem;
        background: white;
        border-radius: 12px;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        color: #333;
    }

    .header {
        text-align: center;
        margin-bottom: 1.5rem;
    }

    .header h2 {
        color: #14695b;
        font-size: 1.8rem;
        margin-bottom: 0.25rem;
    }

    .header p {
        color: #666;
        font-size: 1rem;
        margin: 0;
    }

    .panel {
        border: 1px solid #e9ecef;
        border-radius: 10px;
        padding: 1.25rem;
        margin-bottom: 1.5rem;
    }

    .panel h3 {
        margin: 0 0 1rem 0;
        color: #14695b;
        font-size: 1.15rem;
    }

    .count {
        color: #888;
        font-weight: normal;
        font-size: 0.95rem;
    }

    .form-group {
        margin-bottom: 1rem;
        position: relative;
    }

    .form-group label {
        display: block;
        font-weight: 600;
        color: #14695b;
        margin-bottom: 0.4rem;
        font-size: 0.95rem;
    }

    .required {
        color: #dc3545;
    }

    input {
        width: 100%;
        padding: 0.7rem;
        border: 1px solid #ddd;
        border-radius: 8px;
        font-size: 1rem;
        box-sizing: border-box;
    }

    input:focus {
        outline: none;
        border-color: #14695b;
        box-shadow: 0 0 0 2px rgba(20, 105, 91, 0.15);
    }

    .user-search-container {
        position: relative;
    }

    .search-results {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        background: white;
        border: 1px solid #ddd;
        border-top: none;
        border-radius: 0 0 6px 6px;
        max-height: 240px;
        overflow-y: auto;
        z-index: 1000;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    .search-result-item {
        padding: 0.6rem 0.75rem;
        cursor: pointer;
        border-bottom: 1px solid #f0f0f0;
    }

    .search-result-item:hover {
        background-color: #f8f9fa;
    }

    .search-result-item:last-child {
        border-bottom: none;
    }

    .user-info {
        font-weight: 500;
        color: #333;
        font-size: 0.9rem;
    }

    .user-email {
        font-size: 0.8rem;
        color: #666;
    }

    .member-warning {
        font-size: 0.78rem;
        color: #a15c00;
        margin-top: 0.15rem;
    }

    .form-help {
        font-size: 0.85rem;
        color: #666;
        margin-top: 0.25rem;
    }

    .chips {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        margin: 0.5rem 0 1rem 0;
    }

    .chip {
        display: inline-flex;
        align-items: center;
        gap: 0.35rem;
        background-color: #d1ecf1;
        color: #0c5460;
        padding: 0.35rem 0.6rem;
        border-radius: 16px;
        font-size: 0.88rem;
    }

    .chip-static {
        background-color: #e8f5e9;
        color: #1b5e20;
    }

    .chip-warning {
        cursor: help;
    }

    .chip-remove {
        background: none;
        border: none;
        color: inherit;
        cursor: pointer;
        font-weight: bold;
        font-size: 1rem;
        line-height: 1;
        padding: 0;
    }

    .family-list {
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }

    .family-card {
        border: 1px solid #eee;
        border-radius: 8px;
        padding: 1rem;
        background: #fafafa;
    }

    .family-head {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 1rem;
        margin-bottom: 0.5rem;
    }

    .family-name {
        font-weight: 700;
        color: #14695b;
        font-size: 1.05rem;
    }

    .card-actions {
        display: flex;
        gap: 0.5rem;
        margin-top: 0.5rem;
    }

    .btn {
        padding: 0.55rem 1rem;
        border: none;
        border-radius: 8px;
        font-size: 0.95rem;
        font-weight: 600;
        cursor: pointer;
        transition: background-color 0.2s ease;
    }

    .btn:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    .btn-primary {
        background-color: #14695b;
        color: white;
    }

    .btn-primary:hover:not(:disabled) {
        background-color: #0f5a4f;
    }

    .btn-secondary {
        background-color: #e9ecef;
        color: #333;
    }

    .btn-secondary:hover:not(:disabled) {
        background-color: #dcdfe2;
    }

    .btn-danger {
        background-color: #dc3545;
        color: white;
    }

    .btn-danger:hover:not(:disabled) {
        background-color: #bd2130;
    }

    .success-message {
        background-color: #d4edda;
        color: #155724;
        padding: 0.9rem;
        border-radius: 8px;
        margin-bottom: 1rem;
        border: 1px solid #c3e6cb;
    }

    .error-message {
        background-color: #f8d7da;
        color: #721c24;
        padding: 0.9rem;
        border-radius: 8px;
        margin-bottom: 1rem;
        border: 1px solid #f5c6cb;
    }

    .muted {
        color: #777;
        font-size: 0.9rem;
    }
</style>
